import React, { useEffect } from "react"
import { RegisterError } from "#/components/views/register/RegisterError"
import { EmailInUseByPartner } from "#/components/views/register/EmailInUseByPartner"
import { RegistrationEmailAlreadyInUse } from "#/components/views/register/RegistrationEmailAlreadyInUse"
import { NiceToSeeYou } from "#/components/views/register/NiceToSeeYou"
import { ConfirmAccountEmail } from "#/components/views/register/ConfirmAccountEmail"
import { SetPassword } from "#/components/views/register/SetPassword"
import { AcknowledgeTerms } from "#/components/views/register/AcknowledgeTerms"
import { HealthConsent } from "#/components/views/register/HealthConsent"
import { EmailOptIn, getIsDefaultEmailOptIn } from "./EmailOptIn"
import { PersonalEmail } from "./PersonalEmail"
import { PreferredName } from "./PreferredName"
import { PersonalEmailVerification } from "./PersonalEmailVerification"
import { RegisteringLoadingIndicator } from "./RegistrationLoadingIndicator"
import { WhatKindOfJourney } from "./WhatKindOfJourney"
import { PhoneNumberCollection } from "./PhoneNumberCollection"
import { RegistrationComplete } from "./RegistrationComplete"
import { PregnancyMedicalRecordsCollection, ArtMedicalRecordsCollection } from "./MedicalRecordsCollection"
import { WhereAreYouInJourney } from "#/components/views/register/WhereAreYouInJourney"
import NumberOfChildren from "#/components/views/register/NumberofChildren"
import { DueDateStep } from "#/components/views/register/DueDateStep"
import { DeliveryDateStep } from "#/components/views/register/DeliveryDateStep"
import { STEPS, useRegistration } from "#/components/views/register/RegistrationContext"
import { useRegistrationNavigation } from "./hooks/useRegistrationNavigation"
import { useFetchRegisteringUser } from "#/components/views/register/useFetchRegisteringUser"
import { useHistory } from "react-router-dom"
import { useLDClient } from "launchdarkly-react-client-sdk"
import { isHttpError, parseErrorBody, HttpErrors } from "#/utils/HttpErrors"
import { CarrotErrorCodes } from "#/utils/CarrotErrors"
import { reportError } from "#/utils/ErrorReporting"
import Helpers from "#/utils/Helpers"
import ResponseError from "#/types/responseError"
import { LaunchDarklyUser } from "#/types/launchDarklyUser"
import { useEnabledJourneyOptions } from "#/components/carrot-plans/hooks/useJourneyOptions"
import { RTXRewardsOptIn } from "#/components/views/register/RTXRewardsOptIn"
import AddressStep from "#/components/views/register/AddressStep"

export interface RegistrationJourney {
  journeyType: string
  somethingElseJourney: string
  journeyStage: string
  numberOfChildren: string
  skipped: boolean
  dueDate: string | null
  deliveryDate: string | null
}

const RegisterWizard = (): JSX.Element => {
  const history = useHistory()
  const ldClient = useLDClient()
  const {
    guid,
    step,
    setStep,
    personalEmail,
    preferredName,
    setPreferredName,
    address,
    phoneNumber,
    setPhoneNumber,
    phoneNumberCountryCode,
    setPhoneNumberCountryCode,
    allowsMarketingTexts,
    setAllowsMarketingTexts,
    setDbFormattedPhoneNumber,
    healthConsent,
    setHealthConsent,
    rtxRewardsOptIn,
    setRtxRewardsOptIn,
    emailOptIn,
    setEmailOptIn,
    readPolicy,
    setReadPolicy,
    readTerms,
    setReadTerms,
    journey,
    journeyStage,
    somethingElseJourney,
    isSubmitting,
    numberOfChildren,
    otherJourneyStage,
    dueDate,
    deliveryDate,
    registrationErrorEmail,
    setRegistrationErrorEmail
  } = useRegistration()

  const registeringUserQuery = useFetchRegisteringUser(guid)
  const registeringUser = registeringUserQuery.data
  const registeringUserError = registeringUserQuery.error

  useEffect(() => {
    if (!registeringUser) return
    if (!registeringUser.email) {
      history.push("/signup")
      return
    }
    setPreferredName(registeringUser.preferredName)
    setEmailOptIn(getIsDefaultEmailOptIn(registeringUser.countryCode))
    setPhoneNumberCountryCode(registeringUser.countryCode)
    setStep(STEPS.INTRO)
  }, [registeringUser, history, setPreferredName, setEmailOptIn, setPhoneNumberCountryCode, setStep])

  useEffect(() => {
    if (!registeringUserError) return
    async function handleError() {
      const errorName = (registeringUserError as ResponseError)?.name
      if ([HttpErrors.NOT_FOUND, "FetchError"].includes(errorName)) {
        Helpers.browserRedirect("/")
        return
      }
      if (errorName === HttpErrors.UNAUTHORIZED) {
        Helpers.browserRedirect(`/?errorCode=${CarrotErrorCodes.EMPLOYEE_ALREADY_REGISTERED}`)
        return
      }
      if (isHttpError(registeringUserError)) {
        const body = await parseErrorBody(registeringUserError)
        setRegistrationErrorEmail(body?.email ?? null)
        if (body?.code === CarrotErrorCodes.REGISTRATION_EMAIL_IN_USE_BY_PARTNER) {
          setStep(STEPS.EMAIL_IN_USE_BY_PARTNER)
          return
        }
        if (body?.code === CarrotErrorCodes.REGISTRATION_EMAIL_ALREADY_IN_USE) {
          setStep(STEPS.EMAIL_ALREADY_IN_USE)
          return
        }
      }
      reportError(registeringUserError)
      setStep(STEPS.ERROR)
    }
    void handleError()
  }, [registeringUserError, setStep, setRegistrationErrorEmail])

  useEffect(() => {
    async function loadLDFlagsForRegisteringUser(
      memberEmail: string,
      memberCompanyId: number,
      memberParentCompanyId?: number,
      companyCountryCode?: string
    ): Promise<void> {
      if (!ldClient || !memberEmail || !memberCompanyId) return

      await ldClient.waitUntilReady()

      const ldUser: LaunchDarklyUser = {
        key: memberEmail,
        email: memberEmail,
        custom: {
          companyId: memberCompanyId.toString(),
          countryCode: companyCountryCode,
          isPartner: false,
          isEnrolled: false,
          isEngaged: false
        }
      }

      if (memberParentCompanyId && memberParentCompanyId !== 0) {
        ldUser.custom["parentCompanyId"] = memberParentCompanyId?.toString()
      }

      await ldClient.identify(ldUser)
    }

    const { email, companyId, parentCompanyId, countryCode } = registeringUser || {}

    loadLDFlagsForRegisteringUser(email, companyId, parentCompanyId, countryCode).catch((error) => reportError(error))
  }, [ldClient, registeringUser])

  const {
    onCompleteIntro,
    onBackConfirmAccountEmail,
    onCompleteConfirmAccountEmail,
    onBackSetPassword,
    onCompleteSetPassword,
    onBackPersonalEmail,
    onCompletePersonalEmail,
    onBackEmailOptIn,
    onCompleteEmailOptIn,
    onBackPreferredName,
    onCompletePreferredName,
    onBackAddress,
    onCompleteAddress,
    onBackPhoneNumberCollection,
    onCompletePhoneNumberCollection,
    onBackAcknowledgeTerms,
    onCompleteAcknowledgeTerms,
    onBackHealthConsent,
    onCompleteHealthConsent,
    onBackRTXRewardsOptIn,
    onCompleteRTXRewardsOptIn,
    onBackJourney,
    onCompleteJourney,
    onBackNumberOfChildren,
    onCompleteNumberOfChildren,
    onBackJourneyStage,
    onCompleteJourneyStage,
    onCompleteVerifyPersonalEmail,
    onCompleteMedicalRecordsCollection,
    onBackDueDate,
    onCompleteDueDate,
    onBackDeliveryDate,
    onCompleteDeliveryDate
  } = useRegistrationNavigation(registeringUser)

  const { journeyOptions } = useEnabledJourneyOptions(registeringUser?.benefit)

  switch (step) {
    case STEPS.INTRO:
      return (
        <NiceToSeeYou
          companyDisplayName={registeringUser?.companyDisplayName}
          onComplete={onCompleteIntro}
          benefit={registeringUser?.benefit}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.CONFIRM_ACCOUNT_EMAIL:
      return (
        <ConfirmAccountEmail
          email={registeringUser?.email}
          onBack={onBackConfirmAccountEmail}
          onComplete={onCompleteConfirmAccountEmail}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.SET_PASSWORD:
      return (
        <SetPassword
          email={registeringUser?.email}
          onBack={onBackSetPassword}
          onComplete={onCompleteSetPassword}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.PERSONAL_EMAIL:
      return (
        <PersonalEmail
          personalEmail={personalEmail || ""}
          email={registeringUser?.email}
          onBack={onBackPersonalEmail}
          onComplete={onCompletePersonalEmail}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.PREFERRED_NAME:
      return (
        <PreferredName
          {...{ setPreferredName, preferredName }}
          onBack={onBackPreferredName}
          onComplete={onCompletePreferredName}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.ADDRESS:
      return (
        <AddressStep
          isUsa={registeringUser?.isUsa}
          address={address}
          onBack={onBackAddress}
          onComplete={onCompleteAddress}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.PHONE_NUMBER_COLLECTION:
      return (
        <PhoneNumberCollection
          {...{
            setPhoneNumber,
            phoneNumber,
            setPhoneNumberCountryCode,
            phoneNumberCountryCode,
            setAllowsMarketingTexts,
            setDbFormattedPhoneNumber,
            allowsMarketingTexts
          }}
          isUsa={registeringUser?.isUsa}
          onBack={onBackPhoneNumberCollection}
          onComplete={onCompletePhoneNumberCollection}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.ACKNOWLEDGE_TERMS:
      return (
        <AcknowledgeTerms
          {...{ readPolicy, setReadPolicy, readTerms, setReadTerms }}
          firstName={registeringUser?.firstName}
          preferredName={preferredName}
          onBack={onBackAcknowledgeTerms}
          onComplete={onCompleteAcknowledgeTerms}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.HEALTH_CONSENT:
      return (
        <HealthConsent
          isSubmitting={isSubmitting}
          healthConsent={healthConsent}
          setHealthConsent={setHealthConsent}
          benefit={registeringUser?.benefit}
          onBack={onBackHealthConsent}
          onComplete={onCompleteHealthConsent}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.RTX_REWARDS_OPT_IN:
      return (
        <RTXRewardsOptIn
          isSubmitting={isSubmitting}
          rtxRewardsOptIn={rtxRewardsOptIn}
          setRtxRewardsOptIn={setRtxRewardsOptIn}
          onBack={onBackRTXRewardsOptIn}
          onComplete={onCompleteRTXRewardsOptIn}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.EMAIL_OPT_IN:
      return (
        <EmailOptIn
          isSubmitting={isSubmitting}
          emailOptIn={emailOptIn}
          setEmailOptIn={setEmailOptIn}
          onBack={onBackEmailOptIn}
          onComplete={onCompleteEmailOptIn}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.PERSONAL_EMAIL_VERIFICATION:
      return (
        <PersonalEmailVerification
          personalEmail={personalEmail}
          onComplete={onCompleteVerifyPersonalEmail}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.WHAT_KIND_OF_JOURNEY:
      return (
        <WhatKindOfJourney
          journey={journey}
          somethingElseJourney={somethingElseJourney}
          journeyOptions={journeyOptions}
          isCarrotLite={registeringUser?.benefit?.program.isCarrotLite}
          onBack={onBackJourney}
          onComplete={onCompleteJourney}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.NUMBER_OF_CHILDREN:
      return (
        <NumberOfChildren
          numberOfChildren={numberOfChildren}
          onBack={onBackNumberOfChildren}
          onComplete={onCompleteNumberOfChildren}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.WHERE_ARE_YOU_IN_JOURNEY:
      return (
        <WhereAreYouInJourney
          memberBenefit={registeringUser?.benefit}
          journey={journey}
          numberOfChildren={numberOfChildren}
          journeyStage={journeyStage}
          otherJourneyStage={otherJourneyStage}
          onBack={() => onBackJourneyStage(journey)}
          onComplete={onCompleteJourneyStage}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.DUE_DATE:
      return (
        <DueDateStep
          dueDate={dueDate}
          onBack={onBackDueDate}
          onComplete={onCompleteDueDate}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.DELIVERY_DATE:
      return (
        <DeliveryDateStep
          deliveryDate={deliveryDate}
          onBack={onBackDeliveryDate}
          onComplete={onCompleteDeliveryDate}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.PREGNANCY_MEDICAL_RECORDS_COLLECTION:
      return (
        <PregnancyMedicalRecordsCollection
          onComplete={onCompleteMedicalRecordsCollection}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.ART_MEDICAL_RECORDS_COLLECTION:
      return (
        <ArtMedicalRecordsCollection
          onComplete={onCompleteMedicalRecordsCollection}
          employeeId={registeringUser?.employeeId}
        />
      )
    case STEPS.REGISTRATION_COMPLETED:
      return <RegistrationComplete employeeId={registeringUser?.employeeId} />
    case STEPS.ERROR:
      return <RegisterError employeeId={registeringUser?.employeeId} />
    case STEPS.EMAIL_IN_USE_BY_PARTNER:
      return <EmailInUseByPartner email={registrationErrorEmail ?? registeringUser?.email} />
    case STEPS.EMAIL_ALREADY_IN_USE:
      return <RegistrationEmailAlreadyInUse email={registrationErrorEmail ?? registeringUser?.email} />
    default:
      return <RegisteringLoadingIndicator />
  }
}

export default RegisterWizard
